import React from 'react';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import TripleInput from './TripleInput';

/**
 * PageContentTabs - editor for site_content per page.
 * Props:
 *  - page: 'home' | 'about' | 'menu' | 'gallery' | 'reservations'
 *  - content: siteContent state
 *  - setContent: setter
 *  - onSave: callback to persist (no args)
 *  - saving: bool
 */
const PageContentTabs = ({ page, content, setContent, onSave, saving }) => {
  if (!content) return null;

  const Section = ({ title, children }) => (
    <div className="bg-[#121212] border border-white/10 p-5 space-y-4">
      <h3 className="text-[#d4af37] text-sm font-medium uppercase tracking-wider border-b border-white/10 pb-3">{title}</h3>
      {children}
    </div>
  );

  const SaveBar = () => (
    <div className="flex justify-end sticky bottom-0 bg-[#0a0a0a]/95 backdrop-blur py-3 -mx-2 px-2 border-t border-white/5">
      <Button onClick={onSave} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white px-8" data-testid="page-content-save-btn">
        <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer'}
      </Button>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
      {page === 'home' && (
        <>
          <Section title="Section Hero (Accueil)">
            <TripleInput content={content} setContent={setContent} field="hero_subtitle" label="Sous-titre (affiché au-dessus du titre BÉNI)" />
          </Section>
          <Section title="Section À Propos (sur la page Accueil)">
            <p className="text-white/40 text-xs">Cette section utilise les textes de la page "À Propos". Modifiez-les dans l'onglet correspondant.</p>
          </Section>
          <SaveBar />
        </>
      )}

      {page === 'about' && (
        <>
          <Section title="Section Hero">
            <TripleInput content={content} setContent={setContent} field="about_subtitle_short" label="Sous-titre (au-dessus du titre)" />
            <TripleInput content={content} setContent={setContent} field="about_title" label="Titre principal" />
            <TripleInput content={content} setContent={setContent} field="about_intro" label="Intro" multiline rows={2} />
          </Section>

          <Section title="Section Histoire">
            <TripleInput content={content} setContent={setContent} field="about_history_title" label="Titre" />
            <TripleInput content={content} setContent={setContent} field="about_history_p1" label="Paragraphe 1" multiline rows={3} />
            <TripleInput content={content} setContent={setContent} field="about_history_p2" label="Paragraphe 2" multiline rows={3} />
          </Section>

          <Section title="Section Chef">
            <TripleInput content={content} setContent={setContent} field="about_chef_title" label="Sous-titre" />
            <div className="space-y-2">
              <Label className="text-xs text-white/60 uppercase tracking-wide">Nom du Chef</Label>
              <Input value={content.chef_name || ''} onChange={(e) => setContent({...content, chef_name: e.target.value})} className="bg-transparent border-white/20 text-white text-sm h-9" data-testid="chef-name-input" />
            </div>
            <TripleInput content={content} setContent={setContent} field="about_text" label="Texte de présentation du chef" multiline rows={3} />
            <TripleInput content={content} setContent={setContent} field="about_quote" label="Citation" multiline rows={3} />
          </Section>

          <Section title="Section Philosophie">
            <TripleInput content={content} setContent={setContent} field="about_philosophy_title" label="Titre" />
            <TripleInput content={content} setContent={setContent} field="about_philosophy_text" label="Texte" multiline rows={3} />
            <TripleInput content={content} setContent={setContent} field="about_cta" label="Bouton CTA (vers Menu)" />
          </Section>
          <SaveBar />
        </>
      )}

      {page === 'menu' && (
        <>
          <Section title="Section Hero">
            <TripleInput content={content} setContent={setContent} field="menu_subtitle" label="Sous-titre" />
            <TripleInput content={content} setContent={setContent} field="menu_title" label="Titre principal" />
          </Section>

          <Section title="Menu de la Semaine">
            <p className="text-white/40 text-xs">Le titre est composé de 3 parties : <code className="text-[#d4af37]">[Titre] [Italique] [Fin]</code> → Ex: "Menu midi de la Semaine"</p>
            <TripleInput content={content} setContent={setContent} field="menu_weekly_title" label="Titre (1ère partie)" />
            <TripleInput content={content} setContent={setContent} field="menu_weekly_midi" label="Italique central (ex: midi)" />
            <TripleInput content={content} setContent={setContent} field="menu_weekly_end" label="Fin (ex: de la Semaine)" />
            <TripleInput content={content} setContent={setContent} field="menu_weekly_subtitle" label="Sous-titre" />
          </Section>

          <Section title="Formules (titres affichés sous les prix)">
            <TripleInput content={content} setContent={setContent} field="menu_formulas" label="Titre de la section Formules" />
            <TripleInput content={content} setContent={setContent} field="menu_formula_full" label="Formule complète (Entrée+Plat+Dessert)" />
            <TripleInput content={content} setContent={setContent} field="menu_formula_ep" label="Entrée + Plat" />
            <TripleInput content={content} setContent={setContent} field="menu_formula_pd" label="Plat + Dessert" />
            <TripleInput content={content} setContent={setContent} field="menu_formula_po" label="Plat seul" />
          </Section>

          <Section title="Catégories de plats (badges)">
            <TripleInput content={content} setContent={setContent} field="menu_dishcat_entree" label="Entrée" />
            <TripleInput content={content} setContent={setContent} field="menu_dishcat_meat" label="Viande" />
            <TripleInput content={content} setContent={setContent} field="menu_dishcat_vegetarian" label="Végétarien" />
            <TripleInput content={content} setContent={setContent} field="menu_dishcat_seafood" label="Poisson / Fruits de Mer" />
            <TripleInput content={content} setContent={setContent} field="menu_dishcat_dessert" label="Dessert" />
          </Section>

          <Section title="Bas de page">
            <TripleInput content={content} setContent={setContent} field="menu_price_note" label="Note sur les prix" multiline rows={2} />
            <TripleInput content={content} setContent={setContent} field="menu_reserve_cta" label="Bouton réservation" />
          </Section>
          <SaveBar />
        </>
      )}

      {page === 'gallery' && (
        <>
          <Section title="Section Hero">
            <TripleInput content={content} setContent={setContent} field="gallery_subtitle" label="Sous-titre" />
            <TripleInput content={content} setContent={setContent} field="gallery_title" label="Titre principal" />
          </Section>

          <Section title="Filtres de catégories">
            <TripleInput content={content} setContent={setContent} field="gallery_cat_all" label="Tout" />
            <TripleInput content={content} setContent={setContent} field="gallery_cat_ambiance" label="Ambiance" />
            <TripleInput content={content} setContent={setContent} field="gallery_cat_dishes" label="Plats" />
            <TripleInput content={content} setContent={setContent} field="gallery_cat_team" label="Équipe" />
          </Section>
          <SaveBar />
        </>
      )}

      {page === 'reservations' && (
        <>
          <Section title="Section Hero">
            <TripleInput content={content} setContent={setContent} field="reservations_subtitle" label="Sous-titre" />
            <TripleInput content={content} setContent={setContent} field="reservations_title" label="Titre principal" />
          </Section>

          <Section title="Messages contextuels">
            <TripleInput content={content} setContent={setContent} field="reservations_closed_message" label="Réservations désactivées" multiline rows={2} />
            <TripleInput content={content} setContent={setContent} field="reservations_dateblocked" label="Date bloquée (jour fermé)" multiline rows={2} />
            <TripleInput content={content} setContent={setContent} field="reservations_contact_text" label="Texte avant les coordonnées" />
          </Section>

          <Section title="Écran de confirmation">
            <TripleInput content={content} setContent={setContent} field="reservations_success_text" label="Message après envoi" multiline rows={2} />
          </Section>

          <Section title="Section Informations Utiles (bas de page)">
            <TripleInput content={content} setContent={setContent} field="reservations_info_title" label="Titre" />
            <TripleInput content={content} setContent={setContent} field="reservations_info_hours" label="Étiquette Horaires" />
            <TripleInput content={content} setContent={setContent} field="reservations_info_contact" label="Étiquette Contact" />
            <TripleInput content={content} setContent={setContent} field="reservations_info_address" label="Étiquette Adresse" />
          </Section>
          <SaveBar />
        </>
      )}
    </motion.div>
  );
};

export default PageContentTabs;
